/**
 * Ein Stueck Gelaende als Schraegbild: die Oberflaeche von schraeg oben, darunter
 * ein Bodenblock mit zwei sichtbaren Profilwaenden (Oberboden, Unterboden).
 *
 * Gebraucht wird das an mehreren Stellen der Relief-Hilfe: fuer die Woelbung,
 * die Hangneigung und die Einbindung in die Nachbarschaft. Der Unterschied
 * zwischen ihnen ist NUR die Hoehenfunktion - alles andere (Blickwinkel,
 * Beleuchtung, Bodenblock) ist gleich, damit die Bilder untereinander
 * vergleichbar bleiben.
 *
 * Gezeichnet wird als Hoehenfeld in kleinen Vierecken (Facetten), jedes mit
 * eigener Helligkeit nach seiner Neigung zum Licht. Das ergibt ohne
 * Zusatzbibliothek einen brauchbaren raeumlichen Eindruck.
 *
 * Der Bodenblock ist der Ankerpunkt: Ohne ihn schwebt das Gelaendestueck in der
 * Luft (Nutzer-Befund 10.09.2026); mit ihm steht es auf dem Boden, um den es
 * hier geht.
 */

/**
 * Zeichenflaeche der Schraegbilder (Breite x Hoehe). Hoch genug fuer den
 * steilsten Hang: Bei ueber 60 Grad schlaegt der Block weit nach oben und
 * unten aus, und abgeschnitten waere das Bild wertlos.
 */
export const BLOCK_VIEWBOX = '0 0 56 54';

/** Wie fein die Flaeche zerlegt wird (Facetten je Richtung). */
const FELDER = 6;

/** Wie viel dunkler die Kanten des Netzes sind (Prozentpunkte Helligkeit). */
const KANTE_DUNKLER = 14;

/** Dicke des Oberbodenstreifens in der Profilwand. */
const OBERBODEN_DICKE = 0.22;
/** Wie weit der Block unter den tiefsten Punkt der vorderen Kanten reicht. */
const BLOCK_TIEFE = 0.4;

/**
 * Farbe der Gelaendeoberflaeche. Gruen statt neutralem Blau, weil das Bild ein
 * Stueck Gelaende zeigt (drei Toene nebeneinander gerendert und verglichen,
 * 10.09.2026).
 */
const GELAENDE = { ton: 95, saettigung: 26 };

/** Erdfarben der Profilwaende (Farbton, Saettigung, Grundhelligkeit). */
const OBERBODEN = { ton: 28, saettigung: 26, helligkeit: 32 };
const UNTERBODEN = { ton: 34, saettigung: 34, helligkeit: 54 };
/** Die linke Wand ist vom Licht abgewandt, die rechte zugewandt. */
const WAND_LINKS_DUNKLER = 9;
const WAND_RECHTS_HELLER = 4;

/** Groesse und Lage in der Zeichenflaeche. */
const MASSSTAB = 11;
const HOEHE_MASSSTAB = 11;
const MITTE_X = 28;
const MITTE_Y = 21;

/** Richtung, aus der das Licht kommt (in der Welt aus quer / laengs / hoch). */
const LICHT = normiere([0.45, -0.35, 0.82]);

export interface Facette {
	/** Linienzug des Vierecks. */
	d: string;
	/** Fuellfarbe mit der Helligkeit aus der Neigung. */
	farbe: string;
	/**
	 * Farbe der Kante, etwas dunkler als die Flaeche. Das feine Netz macht die
	 * Form deutlich sichtbar - ohne es sehen die Flaechen verwaschen aus
	 * (nebeneinander gerendert und verglichen, 10.09.2026).
	 */
	kante: string;
	/** Woher das Stueck kommt: die Gelaendeoberflaeche oder der Bodenblock darunter. */
	art: 'gelaende' | 'boden';
}

/**
 * Hoehe an einer Stelle des Blocks.
 *
 * @param laengs Lage in Gefaellrichtung, -1 = bergauf/hinten, +1 = bergab/vorn
 * @param quer   Lage quer zum Hang, -1 bis +1
 */
export type Hoehenfunktion = (laengs: number, quer: number) => number;

/**
 * Die Facetten eines Gelaendeblocks: erst der Bodenblock, dann die Oberflaeche
 * von hinten nach vorn. In dieser Reihenfolge gezeichnet verdeckt das Vordere
 * das Hintere richtig (bei einem Hoehenfeld ohne Ueberhaenge reicht das).
 */
export function blockFacetten(hoehe: Hoehenfunktion): Facette[] {
	const oberflaeche: (Facette & { tiefe: number })[] = [];

	for (let i = 0; i < FELDER; i++) {
		for (let j = 0; j < FELDER; j++) {
			const l0 = -1 + (2 * i) / FELDER;
			const l1 = -1 + (2 * (i + 1)) / FELDER;
			const q0 = -1 + (2 * j) / FELDER;
			const q1 = -1 + (2 * (j + 1)) / FELDER;

			const ecken: [number, number][] = [
				[l0, q0],
				[l0, q1],
				[l1, q1],
				[l1, q0],
			];
			const punkte = ecken.map(([l, q]) => aufDieZeichenflaeche(l, q, hoehe(l, q)));
			const helligkeit = helligkeitFuer(hoehe, (l0 + l1) / 2, (q0 + q1) / 2);

			oberflaeche.push({
				d: 'M' + punkte.map(([x, y]) => `${runde(x)} ${runde(y)}`).join(' L') + ' Z',
				farbe: `hsl(${GELAENDE.ton}, ${GELAENDE.saettigung}%, ${runde(helligkeit)}%)`,
				kante: `hsl(${GELAENDE.ton}, ${GELAENDE.saettigung}%, ${runde(helligkeit - KANTE_DUNKLER)}%)`,
				art: 'gelaende',
				tiefe: i + j,
			});
		}
	}

	// Hinten liegt, was im Bild weiter oben ankommt: kleine Summe aus beiden
	// Grundrichtungen. Der Block kommt zuerst, er haengt unter den vorderen
	// Kanten und darf die Oberflaeche nicht ueberdecken.
	return [
		...bodenblock(hoehe),
		...oberflaeche.sort((a, b) => a.tiefe - b.tiefe).map(({ d, farbe, kante, art }) => ({ d, farbe, kante, art })),
	];
}

/**
 * Die beiden vorderen Profilwaende, je zweigeteilt in Oberboden und Unterboden.
 *
 * „Vorn“ sind in dieser Ansicht die Kanten bei laengs = +1 (links, der
 * Hangfuss) und quer = +1 (rechts). Sie treffen sich im untersten Punkt des
 * Bildes.
 */
function bodenblock(hoehe: Hoehenfunktion): Facette[] {
	const schritte = FELDER;
	const kanteLinks: [number, number][] = [];
	const kanteRechts: [number, number][] = [];
	for (let k = 0; k <= schritte; k++) {
		const t = -1 + (2 * k) / schritte;
		kanteLinks.push([1, t]); // laengs = 1, quer laeuft
		kanteRechts.push([t, 1]); // quer = 1, laengs laeuft
	}

	const hoehen = [...kanteLinks, ...kanteRechts].map(([l, q]) => hoehe(l, q));
	const basis = Math.min(...hoehen) - BLOCK_TIEFE;

	return [
		...wand(hoehe, kanteLinks, basis, -WAND_LINKS_DUNKLER),
		...wand(hoehe, kanteRechts, basis, WAND_RECHTS_HELLER),
	];
}

/** Eine Profilwand: oben der Oberbodenstreifen, darunter der Unterboden. */
function wand(hoehe: Hoehenfunktion, kante: [number, number][], basis: number, helligkeitsversatz: number): Facette[] {
	const oben = kante.map(([l, q]) => aufDieZeichenflaeche(l, q, hoehe(l, q)));
	const unterOberboden = kante.map(([l, q]) => aufDieZeichenflaeche(l, q, hoehe(l, q) - OBERBODEN_DICKE));
	const unten = kante.map(([l, q]) => aufDieZeichenflaeche(l, q, basis));

	const erdfarbe = (schicht: typeof OBERBODEN, zusatz: number) =>
		`hsl(${schicht.ton}, ${schicht.saettigung}%, ${runde(schicht.helligkeit + helligkeitsversatz + zusatz)}%)`;

	return [
		{ d: band(oben, unterOberboden), farbe: erdfarbe(OBERBODEN, 0), kante: erdfarbe(OBERBODEN, -6), art: 'boden' },
		{ d: band(unterOberboden, unten), farbe: erdfarbe(UNTERBODEN, 0), kante: erdfarbe(UNTERBODEN, -8), art: 'boden' },
	];
}

/** Flaeche zwischen zwei Linienzuegen (oben hin, unten zurueck). */
function band(oben: [number, number][], unten: [number, number][]): string {
	const hin = oben.map(([x, y]) => `${runde(x)} ${runde(y)}`).join(' L');
	const zurueck = [...unten].reverse().map(([x, y]) => `${runde(x)} ${runde(y)}`).join(' L');
	return `M${hin} L${zurueck} Z`;
}

/**
 * Schraege Aufsicht: Beide Grundrichtungen laufen von der Bildmitte nach
 * unten weg (laengs nach links-unten, quer nach rechts-unten), die Hoehe
 * hebt den Punkt an.
 */
function aufDieZeichenflaeche(laengs: number, quer: number, hoehe: number): [number, number] {
	const x = MITTE_X + (quer - laengs) * 0.866 * MASSSTAB;
	const y = MITTE_Y + (quer + laengs) * 0.5 * MASSSTAB - hoehe * HOEHE_MASSSTAB;
	return [x, y];
}

/** Helligkeit (in Prozent) aus der Neigung der Facette zum Licht. */
function helligkeitFuer(hoehe: Hoehenfunktion, laengs: number, quer: number): number {
	const d = 0.05;
	const dzLaengs = (hoehe(laengs + d, quer) - hoehe(laengs - d, quer)) / (2 * d);
	const dzQuer = (hoehe(laengs, quer + d) - hoehe(laengs, quer - d)) / (2 * d);

	// Normale des Hoehenfeldes, Reihenfolge wie beim Licht: quer, laengs, hoch
	const normale = normiere([-dzQuer, -dzLaengs, 1]);
	const einfall = Math.max(0, normale[0] * LICHT[0] + normale[1] * LICHT[1] + normale[2] * LICHT[2]);

	return 46 + 38 * einfall;
}

function normiere(v: [number, number, number]): [number, number, number] {
	const laenge = Math.hypot(v[0], v[1], v[2]) || 1;
	return [v[0] / laenge, v[1] / laenge, v[2] / laenge];
}

function runde(wert: number): number {
	return Math.round(wert * 100) / 100;
}
